import type { LocaleCode } from '@ifixit/i18n/types';
import { createRoot } from 'react-dom/client';
import { _js } from '@ifixit/localize';
import { type CartLineItem } from '@ifixit/shopify-cart-sdk';
import { useAddToCart } from '@ifixit/shopify-cart-sdk/hooks';
import { AppProvider } from '@ifixit/app/hooks';
import { initializeReactQuery } from 'Shared/react-query-initialize';
import { ShopifyStorefrontProvider } from '@ifixit/shopify-storefront-client';
import { ViewLangIntlProvider } from 'Shared/use_intl';
import { fireGlobal } from 'Shared/utils/global-events';
import { onDomReady } from 'Shared/utils/dom-ready';
import { ChakraDefaultProvider } from 'Shared/chakra-provider';
import { Button } from '@chakra-ui/react';

onDomReady(() => {
   document.querySelectorAll('.js-step-product-button').forEach(el => {
      const button = el as HTMLElement;
      const root = createRoot(button);
      const lineItemStr = button.dataset.lineitem;
      if (!lineItemStr) {
         return;
      }

      const locale = button.dataset.locale as LocaleCode;
      if (!locale) {
         return;
      }

      const lineItem = JSON.parse(lineItemStr) as CartLineItem;

      const shopifyStoreStr = button.dataset.store;
      if (!shopifyStoreStr) {
         return;
      }

      const shopifyStore = JSON.parse(shopifyStoreStr);

      const priceTiersStr = button.dataset.pricetiers;
      if (!priceTiersStr) {
         return;
      }

      const priceTiers = JSON.parse(priceTiersStr);

      const anchor = button.querySelector('a');
      if (!anchor) {
         return;
      }
      const href = anchor.href;

      root.render(
         initializeReactQuery(
            <ChakraDefaultProvider>
               <ViewLangIntlProvider>
                  <AppProvider
                     ifixitOrigin={window.origin}
                     isLoggedIn={App.isLoggedIn}
                     priceTiers={priceTiers}
                     isOnMobileApp={App.isOnMobileApp}
                     storeLocale={locale}
                  >
                     <ShopifyStorefrontProvider {...shopifyStore}>
                        <AddToCartButton lineItem={lineItem} localeCode={locale} href={href} />
                     </ShopifyStorefrontProvider>
                  </AppProvider>
               </ViewLangIntlProvider>
            </ChakraDefaultProvider>
         )
      );
   });
});

function AddToCartButton({
   lineItem,
   localeCode,
   href,
}: {
   lineItem: CartLineItem;
   localeCode: string;
   href: string;
}) {
   const { addToCart, enabled } = useAddToCart();

   const isItem = addToCart.variables?.lines[0]?.itemcode === lineItem.itemcode;
   const isLoading = addToCart.isPending && isItem;

   return (
      <Button
         _hover={{
            textDecoration: 'none',
         }}
         className="step-product-button"
         isDisabled={!enabled}
         isLoading={isLoading}
         onClick={event => {
            event.preventDefault();
            addToCart.mutate({
               analytics: { descriptor: 'Guide Step', localeCode },
               lines: [lineItem],
            });
            fireGlobal('cart.addcomplete');
         }}
         data-product-href={href}
      >
         {_js('Add To Cart')}
      </Button>
   );
}
